import { Alert, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { BulkEditSummary } from '@/lib/bulkEdit';

interface BulkWarningsAlertProps {
  /** The run so far, holding the warned rows and their warnings grouped by rule, and the rows it refused. */
  summary: BulkEditSummary;
  running: boolean;
  onAcknowledge: () => void;
}

/**
 * A run waiting on its warnings (docs/15-designspecs/Bulk-Edit-And-Actions.md D14, §8.4), for a bulk edit and a bulk
 * command alike: the rows it already refused, named by the row, and the warned rows grouped by rule ("12 row(s): The
 * customer has overdue invoices") with "Acknowledge and run these N".
 */
export function BulkWarningsAlert({ summary, running, onAcknowledge }: BulkWarningsAlertProps) {
  const { t } = useTranslation();
  return (
    <>
      {summary.refusals.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>{t('bulkEdit.refusedTitle')}</Typography>
          {summary.refusals.map((refusal, index) => <div key={index}>{refusal.message}</div>)}
        </Alert>
      )}
      <Alert
        severity="warning"
        sx={{ mb: 2 }}
        action={(
          <Button color="inherit" size="small" disabled={running} onClick={onAcknowledge}>
            {t('bulkEdit.acknowledgeAndRun', { count: summary.warnedRows.length })}
          </Button>
        )}
      >
        <Typography variant="body2" sx={{ mb: 1 }}>{t('bulkEdit.warningsTitle')}</Typography>
        {summary.warnings.map((group) => (
          <div key={group.warning.ruleKey}>
            {t('bulkEdit.warningRows', { count: group.rows.length })}: {group.warning.message}
          </div>
        ))}
      </Alert>
    </>
  );
}
